/**
 * Chromium-family extension packaging and portal UX targets.
 * Recording uses the same MV3 APIs on Chrome and Edge; this module keeps labels and store URLs consistent.
 */
export const EXTENSION_BROWSER_TARGETS = ['chrome', 'edge'];
export const CHROMIUM_BROWSER_TARGET_META = {
    chrome: {
        id: 'chrome',
        brandLabel: 'Chrome',
        productLabel: 'Google Chrome',
        extensionsPageUrl: 'chrome://extensions',
        extensionsPageTitle: 'Chrome Extensions',
        shortcutsPageUrl: 'chrome://extensions/shortcuts',
    },
    edge: {
        id: 'edge',
        brandLabel: 'Edge',
        productLabel: 'Microsoft Edge',
        extensionsPageUrl: 'edge://extensions',
        extensionsPageTitle: 'Edge Extensions',
        shortcutsPageUrl: 'edge://extensions/shortcuts',
    },
};
function normalizeEnvString(value) {
    if (!value)
        return '';
    const t = value.trim();
    if (t.length >= 2) {
        const a = t[0];
        const b = t[t.length - 1];
        if (a === b && (a === '"' || a === "'")) {
            return t.slice(1, -1).trim();
        }
    }
    return t;
}
export function isExtensionBrowserTargetId(value) {
    return value === 'chrome' || value === 'edge';
}
export function parseExtensionBrowserTargetQuery(value, fallback = 'chrome') {
    const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (v === 'edge')
        return 'edge';
    if (v === 'chrome')
        return 'chrome';
    return fallback;
}
/**
 * Best-effort detection for in-browser copy (extensions page, shortcuts). Edge UA contains "Edg/".
 */
export function detectExtensionBrowserTargetFromUserAgent(userAgent) {
    return /\bedg\//i.test(userAgent) ? 'edge' : 'chrome';
}
export function detectExtensionBrowserTargetFromNavigator(navigatorLike) {
    return detectExtensionBrowserTargetFromUserAgent(navigatorLike.userAgent);
}
function tryParseUrl(raw) {
    try {
        return new URL(raw);
    }
    catch {
        return undefined;
    }
}
/** True when `REACT_APP_EXTENSION_INSTALL_URL` points at the Chrome Web Store (one-click install). */
export function isChromeWebStoreInstallUrl(value) {
    const raw = normalizeEnvString(value);
    if (!raw)
        return false;
    const u = tryParseUrl(raw);
    if (!u)
        return false;
    const host = u.hostname.toLowerCase();
    if (host === 'chromewebstore.google.com')
        return true;
    if (host === 'chrome.google.com' && u.pathname.toLowerCase().startsWith('/webstore'))
        return true;
    return false;
}
/** True when the install URL is the Microsoft Edge Add-ons store. */
export function isEdgeAddonsStoreInstallUrl(value) {
    const raw = normalizeEnvString(value);
    if (!raw)
        return false;
    const u = tryParseUrl(raw);
    if (!u)
        return false;
    const host = u.hostname.toLowerCase();
    return host === 'microsoftedge.microsoft.com' && u.pathname.toLowerCase().includes('/addons');
}
/** One-click store install for either Chrome Web Store or Edge Add-ons. */
export function isChromiumExtensionStoreInstallUrl(value) {
    return isChromeWebStoreInstallUrl(value) || isEdgeAddonsStoreInstallUrl(value);
}
/** Which Chromium store the install URL refers to, when it is a known store URL. */
export function inferStoreBrowserTargetFromInstallUrl(value) {
    if (isEdgeAddonsStoreInstallUrl(value))
        return 'edge';
    if (isChromeWebStoreInstallUrl(value))
        return 'chrome';
    return undefined;
}
